import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import LoadingDots from 'Components/LoadingDots';
import PasswordField from 'Components/PasswordField';
import { logException } from 'Utils';
import * as analytics from 'Utils/analytics';
const { EVENTS } = analytics;

const EditPassword = ({
  cancel,
  className,
  confirm,
  currentPassword: currentPasswordText,
  errorMessage: errorMessageText,
  hideText,
  newPassword: newPasswordText,
  onCancel,
  onSubmit,
  passwordLength,
  required,
  showText,
  uim,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validForm, setValidForm] = useState(false);

  const isValidPassword = (password) => uim.passwordRegExp.test(password);

  const isValidField = (value, event) => {
    const inputName = event.target.name;
    let valid = value.length >= 1;
    let message = valid ? '' : required;

    if (inputName === 'new-password') {
      valid = isValidPassword(value);
      message = valid ? '' : passwordLength;
    }

    return {
      valid,
      message,
    };
  };

  const isValidForm = () => {
    // Check if passwords are appropriate and match
    const isValidForm =
      isValidPassword(newPassword) && !!currentPassword.length;
    setValidForm(isValidForm);
  };

  const resetUserPassword = async () => {
    try {
      await uim.getAuthenticatedUser().changePassword({
        currentPassword,
        newPassword,
        newPasswordRepeat: newPassword,
      });
      setSubmitting(false);
      setErrorMessage('');
      onSubmit();
    } catch (err) {
      logException(err);
      setSubmitting(false);
      setErrorMessage(err.message.toString() || errorMessageText);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    analytics.event({ action: EVENTS.submit, label: 'editPassword' });
    setSubmitting(true);
    resetUserPassword();
  };

  useEffect(isValidForm, [currentPassword, newPassword]);

  return (
    <form className={className} onSubmit={handleSubmit}>
      <PasswordField
        autoFocus
        className="password-input"
        failureMessage={errorMessage}
        hideText={hideText}
        id="current-password"
        isValid={isValidField}
        label={currentPasswordText}
        labelClassName="row__label"
        name="current-password"
        onChange={({ target }) => setCurrentPassword(target.value)}
        showText={showText}
        styledMessage
      />
      <PasswordField
        className="password-input"
        hideText={hideText}
        id="new-password"
        isValid={isValidField}
        label={newPasswordText}
        labelClassName="row__label"
        name="new-password"
        onChange={({ target }) => setNewPassword(target.value)}
        showText={showText}
        styledMessage
      />
      <div className="row__buttons">
        <button
          className={`btn--short ${submitting ? 'btn--submitting' : ''}`}
          type="submit"
          disabled={!validForm || submitting}
        >
          {submitting ? <LoadingDots /> : confirm}
        </button>
        <button
          className="btn--short btn--secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          {cancel}
        </button>
      </div>
    </form>
  );
};

EditPassword.propTypes = {
  cancel: PropTypes.string.isRequired,
  className: PropTypes.string,
  confirm: PropTypes.string.isRequired,
  currentPassword: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  hideText: PropTypes.string.isRequired,
  newPassword: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  passwordLength: PropTypes.string.isRequired,
  required: PropTypes.string.isRequired,
  showText: PropTypes.string.isRequired,
  uim: PropTypes.object.isRequired,
};

EditPassword.defaultProps = {
  onCancel: () => {},
};

export default EditPassword;
